import { formatResponse, trimMessageContentHtml } from '../../../utils/helper';
import config, { logger } from '../../../config';
import { getApiHeaders } from '../../../utils/api';
import { getApproximateUserLocationForProto3 } from '../../../utils/approximateUserLocation';
import {
  FINANCIAL_PROFILE_DEFAULTS,
  parseChildrenCount,
  normalizeCollegeYearsArray,
  v2OnboardingResponseRoot,
  normalizeFinancialProfilePatch,
} from '../FinancialProfilePanel';

export const INPUT_MAX_LEN = 2000;
export const RELATIVE_TIME_UPDATE_MS = 30000;
export const MOBILE_MQ = '(max-width: 768px)';
/** Minimum time the typing dots stay up while waiting for an agent reply (fast turns). */
export const V2_TYPING_DOTS_MIN_MS = 4200;

const TIME_UNITS = {
  SECOND: 1000,
  MINUTE: 1000 * 60,
  HOUR: 1000 * 60 * 60,
  DAY: 1000 * 60 * 60 * 24,
  WEEK: 1000 * 60 * 60 * 24 * 7,
  MONTH: 1000 * 60 * 60 * 24 * 30,
  YEAR: 1000 * 60 * 60 * 24 * 365,
};

const PROFILE_COMPLETION_WEIGHTS = {
  welcome_message_answer: 1.2,
  city: 1.5,
  state: 1.5,
  your_year_of_birth: 1.8,
  family_situation: 1.8,
  partner_year_of_birth_of_birth: 1.2,
  children_number: 1.5,
  housing_situation: 1.3,
  duration: 2.1,
  monthly_net_income_during_career_shift: 2.4,
  monthly_net_income_after_career_shift: 2.6,
  monthly_net_income_partner: 1.2,
  monthly_expenses: 2.6,
  retirement_accounts: 2.0,
  other_investments: 1.9,
  total_balance_post_tax_retirement_accounts: 1.7,
  total_balance_equity_compensation_accounts: 1.4,
  total_retirement_account_each_year: 1.6,
  home_estimated_value: 1.0,
  home_mortgage_debt: 1.0,
  current_market_value: 0.9,
  car_loan_debt: 1.0,
  educational_loan: 1.0,
  owe_credit_cards: 1.1,
  other_debts_loans: 0.9,
  retirement_age: 2.2,
  partner_retire: 1.1,
  your_social_security_benefits_age: 1.8,
  partner_social_security_benefits_age: 0.9,
  pension_plan: 1.3,
  pension_income_start_age: 0.8,
  pension_monthly_income: 0.9,
  annuity_plan: 1.1,
  annuity_income_start_age: 0.7,
  annuity_monthly_income: 0.8,
  children_college_savings: 0.9,
};

const PROFILE_COMPLETION_DEFAULT_WEIGHT = 1;
const PROFILE_COMPLETION_COLLEGE_YEAR_WEIGHT = 0.6;

export function awaitMinTypingDotsDisplay(shownAtMs, minMs = V2_TYPING_DOTS_MIN_MS) {
  const remaining = minMs - (Date.now() - shownAtMs);
  if (remaining <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    window.setTimeout(resolve, remaining);
  });
}

export function isPlainObject(x) {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

export function joinPrefixWithTranscript(prefix, transcriptChunk) {
  const p = (prefix ?? '').trimEnd();
  const t = (transcriptChunk ?? '').trim();
  if (!p) return t.slice(0, INPUT_MAX_LEN);
  if (!t) return p.slice(0, INPUT_MAX_LEN);
  return `${p} ${t}`.slice(0, INPUT_MAX_LEN);
}

function isPositiveMoneyString(v) {
  if (v == null) return false;
  const s = String(v).trim();
  if (!s) return false;
  const digits = s.replace(/[^\d]/g, '');
  if (!digits) return false;
  const n = Number.parseInt(digits, 10);
  return Number.isFinite(n) && n > 0;
}

export function hasWord(value, word) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  const escaped = String(word).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(s);
}

export function hasAnsweredValue(value) {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim() !== '';
}

function valuesMatchForCompletion(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!valuesMatchForCompletion(a[i], b[i])) return false;
    }
    return true;
  }
  const left = String(a ?? '').trim().toLowerCase();
  const right = String(b ?? '').trim().toLowerCase();
  return left === right;
}

/**
 * Keep only meaningful answers in local UI state.
 * Defaults are still shown in the panel via fallback rendering, but should not
 * inflate completion before the user/AI provides real input.
 */
export function stripDefaultPrefilledAnswers(profile) {
  if (!isPlainObject(profile)) return {};
  const normalized = normalizeFinancialProfilePatch(profile);
  const out = {};
  for (const [key, value] of Object.entries(normalized)) {
    if (!hasAnsweredValue(value)) continue;
    if (
      Object.prototype.hasOwnProperty.call(FINANCIAL_PROFILE_DEFAULTS, key) &&
      valuesMatchForCompletion(value, FINANCIAL_PROFILE_DEFAULTS[key])
    ) {
      continue;
    }
    out[key] = value;
  }
  return out;
}

function getProfileCompletionWeight(questionId) {
  const w = PROFILE_COMPLETION_WEIGHTS[questionId];
  return Number.isFinite(w) && w > 0 ? w : PROFILE_COMPLETION_DEFAULT_WEIGHT;
}

function isDefaultFilledQuestion(questionId) {
  return hasAnsweredValue(FINANCIAL_PROFILE_DEFAULTS[questionId]);
}

/**
 * Profile completion based on currently visible financial profile questions in `FinancialProfilePanel`.
 * Uses weighted progress for importance + breadth blending for a more human feel.
 */
export function getMandatoryProfileCompletionPercent(profile) {
  const p = profile || {};
  const family = p.family_situation;
  const housing = p.housing_situation;
  const showPartner = hasWord(family, 'married') || hasWord(family, 'partnered');
  const showRealEstateCard = hasWord(housing, 'own');
  const childCount = parseChildrenCount(p.children_number);
  const showCollegeCard = childCount > 0;
  const pensionYes = hasWord(p.pension_plan ?? FINANCIAL_PROFILE_DEFAULTS.pension_plan, 'yes');
  const annuityYes = hasWord(p.annuity_plan ?? FINANCIAL_PROFILE_DEFAULTS.annuity_plan, 'yes');

  const visibleQuestionIds = [
    'welcome_message_answer',
    'city',
    'state',
    'your_year_of_birth',
    'family_situation',
    ...(showPartner ? ['partner_year_of_birth_of_birth'] : []),
    'children_number',
    'housing_situation',
    'duration',
    'monthly_net_income_during_career_shift',
    'monthly_net_income_after_career_shift',
    ...(showPartner ? ['monthly_net_income_partner'] : []),
    'monthly_expenses',
    'retirement_accounts',
    'other_investments',
    'total_balance_post_tax_retirement_accounts',
    'total_balance_equity_compensation_accounts',
    'total_retirement_account_each_year',
    ...(showRealEstateCard ? ['home_estimated_value', 'home_mortgage_debt', 'current_market_value'] : []),
    'car_loan_debt',
    'educational_loan',
    'owe_credit_cards',
    'other_debts_loans',
    'retirement_age',
    ...(showPartner ? ['partner_retire'] : []),
    'your_social_security_benefits_age',
    ...(showPartner ? ['partner_social_security_benefits_age'] : []),
    'pension_plan',
    ...(pensionYes ? ['pension_income_start_age', 'pension_monthly_income'] : []),
    'annuity_plan',
    ...(annuityYes ? ['annuity_income_start_age', 'annuity_monthly_income'] : []),
    ...(showCollegeCard ? ['children_college_savings'] : []),
  ];

  let totalWeight = 0;
  let immediateAnsweredWeight = 0;
  let deferredDefaultAnsweredWeight = 0;
  let nonDefaultRequiredTotalWeight = 0;
  let nonDefaultRequiredAnsweredWeight = 0;
  let immediateAnsweredCount = 0;
  let deferredDefaultAnsweredCount = 0;
  let totalCount = 0;

  visibleQuestionIds.forEach((id) => {
    const defaultValue = FINANCIAL_PROFILE_DEFAULTS[id];
    const hasNonBlankDefault = isDefaultFilledQuestion(id);
    const rawValue = p[id];
    const effectiveValue = hasAnsweredValue(rawValue) ? rawValue : defaultValue;
    const isAnswered = hasAnsweredValue(effectiveValue);
    const isDefaultAnswered =
      hasNonBlankDefault &&
      isAnswered &&
      valuesMatchForCompletion(effectiveValue, defaultValue);
    const weight = getProfileCompletionWeight(id);

    totalWeight += weight;
    totalCount += 1;

    if (!hasNonBlankDefault) {
      nonDefaultRequiredTotalWeight += weight;
      if (isAnswered) {
        nonDefaultRequiredAnsweredWeight += weight;
      }
    }

    if (!isAnswered) return;
    if (isDefaultAnswered) {
      deferredDefaultAnsweredWeight += weight;
      deferredDefaultAnsweredCount += 1;
      return;
    }
    immediateAnsweredWeight += weight;
    immediateAnsweredCount += 1;
  });

  if (showCollegeCard) {
    totalWeight += childCount * PROFILE_COMPLETION_COLLEGE_YEAR_WEIGHT;
    totalCount += childCount;
    const collegeYears = normalizeCollegeYearsArray(p.children_college_years);
    for (let i = 0; i < childCount; i += 1) {
      if (hasAnsweredValue(collegeYears[i])) {
        immediateAnsweredWeight += PROFILE_COMPLETION_COLLEGE_YEAR_WEIGHT;
        immediateAnsweredCount += 1;
        nonDefaultRequiredAnsweredWeight += PROFILE_COMPLETION_COLLEGE_YEAR_WEIGHT;
      }
      nonDefaultRequiredTotalWeight += PROFILE_COMPLETION_COLLEGE_YEAR_WEIGHT;
    }
  }

  if (totalWeight <= 0 || totalCount <= 0) return 0;

  const nonDefaultRequiredDone =
    nonDefaultRequiredTotalWeight <= 0 ||
    nonDefaultRequiredAnsweredWeight >= nonDefaultRequiredTotalWeight - 1e-9;

  const answeredWeight = nonDefaultRequiredDone
    ? immediateAnsweredWeight + deferredDefaultAnsweredWeight
    : immediateAnsweredWeight;
  const answeredCount = nonDefaultRequiredDone
    ? immediateAnsweredCount + deferredDefaultAnsweredCount
    : immediateAnsweredCount;

  const weightedRatio = answeredWeight / totalWeight;
  const breadthRatio = answeredCount / totalCount;
  const blendedRatio = weightedRatio * 0.78 + breadthRatio * 0.22;

  if (answeredCount >= totalCount || answeredWeight >= totalWeight - 1e-9) return 100;
  return Math.max(0, Math.min(100, Math.round(blendedRatio * 100)));
}

function filterMatchingLines(reply, looksLikeQuestion) {
  const lines = String(reply).split('\n');
  return lines.filter((ln) => !looksLikeQuestion.test(ln)).join('\n').trim();
}

function redactMonthlyExpensesQuestionIfAutofilled(agentReply, financialProfilePatch) {
  const reply = String(agentReply || '');
  if (!reply.trim() || !isPlainObject(financialProfilePatch)) return reply;
  if (!isPositiveMoneyString(financialProfilePatch.monthly_expenses)) return reply;

  const looksLikeExpenseQuestion = /(monthly\s+expenses|monthly\s+spend|spend\s+.*per\s+month|expenses\s+per\s+month)/i;
  if (!looksLikeExpenseQuestion.test(reply)) return reply;
  return filterMatchingLines(reply, looksLikeExpenseQuestion);
}

function redactChildrenCollegeYearsQuestionIfAlreadyFilled(agentReply, financialProfilePatch) {
  const reply = String(agentReply || '');
  if (!reply.trim() || !isPlainObject(financialProfilePatch)) return reply;

  const years = normalizeCollegeYearsArray(financialProfilePatch.children_college_years);
  const hasYears = Array.isArray(years) && years.some((x) => String(x ?? '').trim() !== '');
  if (!hasYears) return reply;

  const looksLikeCollegeYearsQuestion =
    /(college\s+start\s+year|college\s+years|start\s+year\s+.*child|for\s+each\s+child.*college)/i;
  if (!looksLikeCollegeYearsQuestion.test(reply)) return reply;
  return filterMatchingLines(reply, looksLikeCollegeYearsQuestion);
}

function redactSpousePartnerQuestionsIfNotCoupled(agentReply, financialProfilePatch) {
  const reply = String(agentReply || '');
  if (!reply.trim() || !isPlainObject(financialProfilePatch)) return reply;

  const family = financialProfilePatch.family_situation;
  const showPartner = hasWord(family, 'married') || hasWord(family, 'partnered');
  if (showPartner) return reply;

  const looksLikeSpousePartnerQuestion = /\b(spouse|partner(?:'s)?)\b/i;
  if (!looksLikeSpousePartnerQuestion.test(reply)) return reply;

  const joined = filterMatchingLines(reply, looksLikeSpousePartnerQuestion);
  return joined || reply.replace(looksLikeSpousePartnerQuestion, '').replace(/\s{2,}/g, ' ').trim();
}

export function sanitizeAgentReply(agentReply, financialProfilePatch) {
  const reply = String(agentReply || '');
  if (!reply.trim()) return reply;
  const afterExpenses = redactMonthlyExpensesQuestionIfAutofilled(reply, financialProfilePatch);
  const afterCollege = redactChildrenCollegeYearsQuestionIfAlreadyFilled(afterExpenses || reply, financialProfilePatch);
  const afterSpouse = redactSpousePartnerQuestionsIfNotCoupled(afterCollege || afterExpenses || reply, financialProfilePatch);
  return afterSpouse || afterCollege || afterExpenses || reply;
}

/** Agent text from the API is markdown/plain; we format to HTML when rendering. */
export function buildAgentMessage(content, ts = Date.now(), options = {}) {
  return {
    id: `agent-${ts}`,
    role: 'agent',
    content,
    ts,
    isHtml: false,
    animateTyping: Boolean(options.animateTyping),
    focusComposerAfter: Boolean(options.focusComposerAfter),
  };
}

export function buildUserMessage(text, ts = Date.now()) {
  return {
    id: `user-${ts}`,
    role: 'user',
    content: text,
    ts,
  };
}

export function formatAgentBubbleHtml(raw) {
  const s = String(raw ?? '');
  if (!s.trim()) return '';
  return trimMessageContentHtml(formatResponse(s));
}

/** Places the caret at the end of the last block so it stays inline with the agent text. */
export function appendTypingCursorToFormattedHtml(html, cursorClass) {
  if (!html || !cursorClass) return html;
  const marker = '</p>';
  const idx = html.lastIndexOf(marker);
  if (idx === -1) return `${html}<span class="${cursorClass}"></span>`;
  return `${html.slice(0, idx)}<span class="${cursorClass}"></span>${html.slice(idx)}`;
}

export function formatRelativeTime(timestamp) {
  const rawTs = timestamp ?? Date.now();
  const date = new Date(rawTs);
  const diffMs = Date.now() - date.getTime();
  if (!Number.isFinite(date.getTime()) || diffMs < 0) return 'Just now';

  const diffSeconds = Math.floor(diffMs / TIME_UNITS.SECOND);
  const diffMinutes = Math.floor(diffMs / TIME_UNITS.MINUTE);
  const diffHours = Math.floor(diffMs / TIME_UNITS.HOUR);
  const diffDays = Math.floor(diffMs / TIME_UNITS.DAY);
  const diffWeeks = Math.floor(diffMs / TIME_UNITS.WEEK);
  const diffMonths = Math.floor(diffMs / TIME_UNITS.MONTH);
  const diffYears = Math.floor(diffMs / TIME_UNITS.YEAR);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
}

export function getLastAgentMessageId(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'agent') return messages[i].id;
  }
  return null;
}

export function getV2OnboardingApiChat(apiResponse) {
  const chat = v2OnboardingResponseRoot(apiResponse)?.v2_onboarding_ai?.chat;
  return Array.isArray(chat) ? chat : [];
}

export function getV2OnboardingDone(apiResponse) {
  return Boolean(v2OnboardingResponseRoot(apiResponse)?.onboarding_ai_done);
}

export function lastV2OnboardingApiChatEntry(apiResponse) {
  const chat = getV2OnboardingApiChat(apiResponse);
  return chat.length ? chat[chat.length - 1] : null;
}

export function parseProto3ApiChat(apiChat) {
  const uiMessages = [];
  const filtered = Array.isArray(apiChat) ? apiChat : [];
  filtered.forEach((m, i) => {
    const ts = m?.timestamp ?? Date.now();
    if (m?.type === 'question') {
      uiMessages.push({
        id: `q-${ts}-${i}`,
        role: 'agent',
        content: m.text || '',
        ts,
        isHtml: false,
      });
    } else if (m?.type === 'answer') {
      uiMessages.push({
        id: `a-${ts}-${i}`,
        role: 'user',
        content: m.text || '',
        ts,
      });
    }
  });
  return { uiMessages };
}

export function serializeChatForApi(uiMessages) {
  if (!Array.isArray(uiMessages)) return [];
  return uiMessages
    .filter((m) => m && (m.role === 'user' || m.role === 'agent'))
    .map((m) => ({
      type: m.role === 'agent' ? 'question' : 'answer',
      text: m.content ?? '',
      timestamp: m.ts ?? Date.now(),
    }));
}

export function getLastAgentQuestionFromUiMessages(uiMessages) {
  if (!Array.isArray(uiMessages)) return '';
  for (let i = uiMessages.length - 1; i >= 0; i -= 1) {
    const m = uiMessages[i];
    if (m?.role !== 'agent') continue;
    const text = typeof m?.content === 'string' ? m.content.trim() : '';
    if (text) return text;
  }
  return '';
}

/** After refresh, focus the composer when the last stored message is an agent question awaiting an answer. */
export function withComposerFocusAfterRestore(uiMessages, { onboardingDone, resumeFromLastAnswer }) {
  if (resumeFromLastAnswer || onboardingDone || !Array.isArray(uiMessages) || !uiMessages.length) {
    return uiMessages;
  }
  const lastIdx = uiMessages.length - 1;
  const last = uiMessages[lastIdx];
  if (last?.role !== 'agent' || last.focusComposerAfter) return uiMessages;
  const next = [...uiMessages];
  next[lastIdx] = { ...last, focusComposerAfter: true };
  return next;
}

/** One shared in-flight/resolved promise so Strict Mode or remounts do not duplicate reverse-geocode. */
let proto3ApproxLocationPromise = null;
let proto3ApproxLocationResolved = null;

export function getProto3ApproximateLocationShared() {
  if (proto3ApproxLocationResolved) {
    return Promise.resolve(proto3ApproxLocationResolved);
  }
  if (!proto3ApproxLocationPromise) {
    proto3ApproxLocationPromise = getApproximateUserLocationForProto3({ timeoutMs: 8000 })
      .then((loc) => {
        if (loc) proto3ApproxLocationResolved = loc;
        return loc;
      })
      .catch(() => null)
      .finally(() => {
        proto3ApproxLocationPromise = null;
      });
  }
  return proto3ApproxLocationPromise;
}

export async function postProto3Turn({ token, message, client_location, continuous_copilot_chat, last_ai_question, signal }) {
  const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.V2_ONBOARDING_AI_PROTO3_TURN}`;
  try {
    const payload = { token, message, client_location };
    if (typeof last_ai_question === 'string' && last_ai_question.trim()) {
      payload.last_ai_question = last_ai_question.trim();
    }
    if (continuous_copilot_chat) {
      payload.continuous_copilot_chat = true;
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
      signal,
    });
    const body = await res.json().catch(() => null);
    const data = body?.data?.data ?? body?.data ?? body ?? null;
    const replyRaw =
      typeof data?.agent_reply === 'string'
        ? data.agent_reply
        : typeof data?.agentReply === 'string'
          ? data.agentReply
          : typeof data?.reply === 'string'
            ? data.reply
            : '';
    return {
      ok: res.ok,
      status: res.status,
      reply: replyRaw,
      updates: data?.financial_profile_updates,
      fullProfile: data?.financial_profile,
      completed: Boolean(data?.onboarding_ai_done),
      body,
    };
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
    logger.error('Proto3 turn failed:', e);
    return {
      ok: false,
      status: 0,
      reply: '',
      updates: null,
      fullProfile: null,
      completed: false,
      body: null,
    };
  }
}

export async function postProto3Start({ token, client_location, signal }) {
  const startUrl = `${config.API_BASE_URL}${config.API_ENDPOINTS.V2_ONBOARDING_AI_PROTO3_START}`;
  const startPayload = { token, client_location };
  let res;
  let body = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (signal?.aborted) break;
    res = await fetch(startUrl, {
      method: 'POST',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(startPayload),
      signal,
    });
    body = await res.json().catch(() => null);
    if (res.ok) break;
    if (res.status === 503 && body?.error_code === 'GEMINI_UNAVAILABLE' && attempt < 2) {
      await new Promise((r) => {
        window.setTimeout(r, 1500 * (attempt + 1));
      });
      continue;
    }
    break;
  }
  return { res, body, reply: body?.data?.agent_reply };
}

export function turnPatchForGuard(turn) {
  if (isPlainObject(turn?.fullProfile)) return turn.fullProfile;
  if (isPlainObject(turn?.updates)) return turn.updates;
  return null;
}

export function applyFinancialProfileFromTurn(turn, setUserAnswers, setFinancialProfileRaw) {
  if (isPlainObject(turn.fullProfile) && Object.keys(turn.fullProfile).length) {
    const meaningful = stripDefaultPrefilledAnswers(turn.fullProfile);
    if (Object.keys(meaningful).length) {
      setUserAnswers((prev) => ({ ...prev, ...meaningful }));
    }
    setFinancialProfileRaw(turn.fullProfile);
    return;
  }
  if (isPlainObject(turn.updates) && Object.keys(turn.updates).length) {
    const patch = normalizeFinancialProfilePatch(turn.updates);
    setUserAnswers((prev) => ({ ...prev, ...patch }));
    setFinancialProfileRaw((prev) => ({ ...(prev || {}), ...patch }));
  }
}

export function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}
